// src/pages/Deployments.jsx
// Console → Deployments (3.60.4, owner, 2026-10-15): how the Fax Portal was
// built, kept current by every deploy. The releases and the changes in them
// come with the portal (releaseNotes.json); the deploy totals, code updates
// and the latest deployments come from the backend (config/deploy_stats and
// deploy_log, written by scripts/recordDeploy.js at the end of ./deploy.sh and
// `npm run deploy`). Super Admins only.
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { API_BASE } from '../firebase/config';
import { timeZone, tzLabel } from '../utils/timeZone';
import notes from '../releaseNotes.json';
import Loader from '../components/Loader';
import ShowSelect from '../components/ShowSelect';
import Pager, { usePager } from '../components/Pager';
import SortTh, { useSort } from '../components/SortTh';
import Emo from '../components/Emo';

const AUD = { all: 'Everyone', admins: 'Admins', managers: 'Managers', super_admins: 'Super Admins', internal: 'Technical (internal)' };
// 3.60.5 (owner): the lists have filters, Show and a pager
const n = v => (v == null ? '—' : Number(v).toLocaleString('en-US'));
export const when = iso => (iso ? new Date(iso).toLocaleString('en-US', { timeZone: timeZone(), month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' ' + tzLabel(new Date(iso)) : '—');
const dayOf = iso => (iso ? new Date(iso).toLocaleDateString('en-US', { timeZone: timeZone(), month: 'short', day: 'numeric', year: 'numeric' }) : '');

export default function Deployments() {
  const { getToken } = useAuth();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  // 3.60.5 (owner): both lists get filters, Show and a pager (never a fixed cap)
  const [q, setQ] = useState('');
  const [month, setMonth] = useState('');
  const [relShow, setRelShow] = useState('50');
  const [openRel, setOpenRel] = useState(null);
  const [logWhat, setLogWhat] = useState('');
  const [logQ, setLogQ] = useState('');
  const [logShow, setLogShow] = useState('50');
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/portal/control/deployments`, { headers: { Authorization: `Bearer ${await getToken()}` } });
        const j = await r.json(); if (!r.ok) throw new Error(j.error);
        setD(j);
      } catch (e) { setError(e.message); }
    })();
  }, [getToken]);

  // From the release notes the portal carries
  const rel = useMemo(() => {
    const list = (notes.releases || []).filter(r => r.releasedAt || r.dateLabel);
    const items = list.reduce((t, r) => t + (r.items || []).length, 0);
    const aud = {};
    for (const r of list) for (const i of r.items || []) aud[i.audience] = (aud[i.audience] || 0) + 1;
    const majors = {};
    for (const r of list) { const m = r.version.split('.')[0]; majors[m] = (majors[m] || 0) + 1; }
    const byMonth = {};
    for (const r of list) { const k = (r.releasedAt || '').slice(0, 7); if (!k) continue; const x = byMonth[k] || (byMonth[k] = { releases: 0, changes: 0 }); x.releases++; x.changes += (r.items || []).length; }
    const first = list[list.length - 1], last = list[0];
    const days = first?.releasedAt ? Math.max(1, Math.round((Date.now() - Date.parse(first.releasedAt)) / 86400000)) : null;
    return { list, items, aud, majors, byMonth, first, last, days };
  }, []);

  const shown = rel.list.filter(r => (!month || (r.releasedAt || '').startsWith(month))
    && (!q || `${r.version} ${r.title} ${(r.items || []).map(i => i.text).join(' ')}`.toLowerCase().includes(q.toLowerCase())));
  const verNum = v => String(v).split('.').reduce((t, x) => t * 1000 + Number(x || 0), 0);
  const relSort = useSort(shown, { version: r => verNum(r.version), released: r => r.releasedAt || '', title: r => r.title || '', changes: r => (r.items || []).length });
  const { pageRows: relRows, pager: relPager } = usePager(relSort.sorted, relShow);
  const logAll = (d?.log || []).filter(x => (!logWhat || x.what === logWhat)
    && (!logQ || [x.version, x.revision, x.commit, x.computer, x.system].some(v => String(v || '').toLowerCase().includes(logQ.toLowerCase()))));
  const logSort = useSort(logAll, { at: x => x.at || '', what: x => x.what || '', version: x => verNum(x.version || '0'), revision: x => x.revision || '' });
  const { pageRows: logRows, pager: logPager } = usePager(logSort.sorted, logShow);
  const s = d?.stats || {};
  const commits = (s.commits?.backend || 0) + (s.commits?.portal || 0);

  return (
    <div>
      {/* The heading and its line come from the Console tab (ControlPanel.jsx) */}
      {error && <div style={S.error}>{error}</div>}

      <div style={S.cards}>
        {[
          ['Versions released', n(rel.list.length), `${rel.first?.version} → ${rel.last?.version}`],
          ['Backend deployments', n(s.backendRevisions), 'Cloud Run versions'],
          ['Portal deployments', n(s.deploys?.portal), 'website releases'],
          ['Changes', n(rel.items), 'in the release notes'],
          ['Code updates', commits ? n(commits) : '—', s.commits ? `portal ${n(s.commits.portal)} · backend ${n(s.commits.backend)}` : ''],
          ['Days since 1.0.0', n(rel.days), rel.first?.releasedAt ? dayOf(rel.first.releasedAt) : ''],
          ['Releases a day', rel.days ? (rel.list.length / rel.days).toFixed(1) : '—', 'on average'],
        ].map(([l, v, sub]) => (
          <div key={l} style={S.card}><div style={S.cardV}>{v}</div><div style={S.cardL}>{l}</div>{sub && <div style={S.cardS}>{sub}</div>}</div>
        ))}
      </div>

      <div style={S.two}>
        <div style={S.box}>
          <div style={S.boxT}>Changes, by who they’re for</div>
          {Object.entries(AUD).map(([k, l]) => <div key={k} style={S.line}><span>{l}</span><strong>{n(rel.aud[k] || 0)}</strong></div>)}
        </div>
        <div style={S.box}>
          <div style={S.boxT}>Releases by month</div>
          {Object.entries(rel.byMonth).sort().reverse().map(([k, x]) => (
            <div key={k} style={S.line}><span>{new Date(`${k}-15T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })}</span><span><strong>{x.releases}</strong> releases · {n(x.changes)} changes</span></div>
          ))}
          <div style={{ ...S.line, borderTop: '1px solid var(--c-e0f2f1)', marginTop: 4 }}><span>By generation</span><span>{Object.entries(rel.majors).sort().map(([m, c]) => `${m}.x: ${c}`).join(' · ')}</span></div>
        </div>
      </div>

      <h4 style={S.h4}>Deployments</h4>
      <div style={S.filters}>
        <select value={logWhat} onChange={e => setLogWhat(e.target.value)} style={S.input} aria-label="What was deployed">
          <option value="">Backend and portal</option><option value="backend">Backend</option><option value="portal">Portal</option>
        </select>
        <input value={logQ} onChange={e => setLogQ(e.target.value)} placeholder="Version, backend version, code or computer" style={S.input} aria-label="Search deployments" />
        <ShowSelect value={logShow} onChange={setLogShow} style={S.input} labelStyle={S.showLabel} groupStyle={S.showGroup} />
      </div>
      {!d && !error ? <Loader /> : (
        <>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr style={S.thead}>
                <SortTh k="at" label="When" sort={logSort.sort} onSort={logSort.onSort} style={S.th}>When</SortTh>
                <SortTh k="what" label="What" sort={logSort.sort} onSort={logSort.onSort} style={S.th}>What</SortTh>
                <SortTh k="version" label="Version" sort={logSort.sort} onSort={logSort.onSort} style={S.th}>Version</SortTh>
                <SortTh k="revision" label="Backend version" sort={logSort.sort} onSort={logSort.onSort} style={S.th}>Backend version</SortTh>
                <th style={S.th}>Code</th><th style={S.th}>From</th>
              </tr></thead>
              <tbody>
                {logRows.length === 0 ? <tr><td colSpan={6} style={{ ...S.td, textAlign: 'center', padding: 24, color: 'var(--t-78909c)' }}>{(d?.log || []).length ? 'No deployments match.' : 'No deployments recorded yet.'}</td></tr>
                  : logRows.map((x, i) => (
                    <tr key={x.id} style={{ ...S.tr, background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)' }}>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{when(x.at)}</td>
                      <td style={S.td}>{x.what === 'backend' ? <><Emo e="⚙️" /> Backend</> : <><Emo e="🖥️" /> Portal</>}</td>
                      <td style={S.td}>{x.version || '—'}</td>
                      <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12 }}>{x.revision ? x.revision.replace('crazypixels-fax-', '') : '—'}</td>
                      <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12 }}>{x.commit || '—'}</td>
                      {/* 3.61.1 (owner): the computer and system it ran from; older rows (filled in from Cloud Run / Firebase history) show the tool */}
                      <td style={{ ...S.td, fontSize: 12 }}>{x.computer ? <>{x.computer}{x.system && <div style={{ color: 'var(--t-90a4ae)', fontSize: 11 }}>{x.system}</div>}</> : x.system ? <span title="Filled in from history: the computer isn't recorded, only the tool">{x.system} <span style={{ color: 'var(--t-90a4ae)' }}>· from history</span></span> : '—'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <Pager {...logPager} label="deployments" />
        </>
      )}

      <h4 style={S.h4}>Every release</h4>
      <div style={S.filters}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search versions and changes" style={{ ...S.input, minWidth: 260 }} aria-label="Search releases" />
        <select value={month} onChange={e => setMonth(e.target.value)} style={S.input} aria-label="Month">
          <option value="">Every month</option>
          {Object.keys(rel.byMonth).sort().reverse().map(k => <option key={k} value={k}>{new Date(`${k}-15T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })}</option>)}
        </select>
        <ShowSelect value={relShow} onChange={setRelShow} style={S.input} labelStyle={S.showLabel} groupStyle={S.showGroup} />
      </div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr style={S.thead}>
            <SortTh k="version" label="Version" sort={relSort.sort} onSort={relSort.onSort} style={S.th}>Version</SortTh>
            <SortTh k="released" label="Released" sort={relSort.sort} onSort={relSort.onSort} style={S.th}>Released</SortTh>
            <SortTh k="title" label="Title" sort={relSort.sort} onSort={relSort.onSort} style={S.th}>Title</SortTh>
            <SortTh k="changes" label="Changes" sort={relSort.sort} onSort={relSort.onSort} style={{ ...S.th, textAlign: 'right' }}>Changes</SortTh>
          </tr></thead>
          <tbody>
            {relRows.length === 0 ? <tr><td colSpan={4} style={{ ...S.td, textAlign: 'center', padding: 24, color: 'var(--t-78909c)' }}>No releases match.</td></tr>
              : relRows.map((r, i) => [
                <tr key={r.version} onClick={() => setOpenRel(openRel === r.version ? null : r.version)} style={{ ...S.tr, background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)', cursor: 'pointer' }}
                  role="button" tabIndex={0} aria-expanded={openRel === r.version} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenRel(openRel === r.version ? null : r.version); } }}>
                  <td style={{ ...S.td, fontWeight: 700, whiteSpace: 'nowrap' }}>{openRel === r.version ? '▾' : '▸'} {r.version}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{r.dateLabel || dayOf(r.releasedAt)}</td>
                  <td style={S.td}>{r.title}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{(r.items || []).length}</td>
                </tr>,
                openRel === r.version && (
                  <tr key={`${r.version}-d`} style={S.tr}><td colSpan={4} style={{ ...S.td, background: 'var(--c-f0faf8)' }}>
                    {(r.items || []).map((it, k) => <div key={k} style={{ fontSize: 12.5, margin: '4px 0' }}><span style={S.aud}>{AUD[it.audience] || it.audience}</span> {it.text}</div>)}
                  </td></tr>
                ),
              ])}
          </tbody>
        </table>
      </div>
      <Pager {...relPager} label="releases" />
    </div>
  );
}

export const S = {
  h4: { margin: '22px 0 8px', color: 'var(--t-00695c)', fontSize: 15 },
  small: { fontSize: 12, color: 'var(--t-78909c)', marginTop: 6 },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '12px 18px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 },
  card: { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: '12px 14px', textAlign: 'center' },
  cardV: { fontSize: 24, fontWeight: 800, color: 'var(--t-00695c)' },
  cardL: { fontSize: 12, color: 'var(--t-546e7a)', fontWeight: 600, marginTop: 2 },
  cardS: { fontSize: 11, color: 'var(--t-90a4ae)', marginTop: 2 },
  two: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 },
  box: { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: '12px 16px' },
  boxT: { fontWeight: 700, color: 'var(--t-37474f)', fontSize: 14, marginBottom: 6 },
  line: { display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13, color: 'var(--t-37474f)', padding: '4px 0' },
  input: { padding: '7px 10px', border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', minWidth: 260, background: 'var(--c-ffffff)', color: 'var(--t-37474f)' },
  filters: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 10 },
  showGroup: { display: 'flex', alignItems: 'center', gap: 6 },
  showLabel: { fontSize: 12, color: 'var(--t-546e7a)', fontWeight: 600 },
  aud: { display: 'inline-block', fontSize: 10.5, fontWeight: 700, color: 'var(--t-00695c)', background: 'var(--c-e0f2f1)', borderRadius: 6, padding: '1px 6px', marginRight: 4 },
  ghost: { padding: '7px 14px', background: 'var(--c-ffffff)', color: 'var(--t-546e7a)', border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  tableWrap: { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--c-00695c)' },
  th: { padding: '11px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid var(--c-f0f0f0)' },
  td: { padding: '9px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'top' },
};
